import React, { useState, useEffect } from 'react';
import {
  Input,
  Button,
  Card,
  CardBody,
  Spinner,
  Switch
} from "@heroui/react";
import { Clock, Calendar, EnvelopeSimple, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";

interface RentalSettings {
  appointmentAdvanceHours: number;
  autoSendImmowebInvitation: boolean;
  autoCloseTerminationPhase: boolean;
}

export const VerhuurTab: React.FC = () => {
  const { session } = useSession();
  const [settings, setSettings] = useState<RentalSettings>({
    appointmentAdvanceHours: 24,
    autoSendImmowebInvitation: false,
    autoCloseTerminationPhase: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchRentalSettings();
  }, []);

  const fetchRentalSettings = async () => {
    try {
      setIsLoading(true);
      const response = await fetchWithAuth(
        `${API_URL}/rental/settings`,
        { method: "GET" },
        session
      );
      
      if (!response.ok) {
        throw new Error("Failed to fetch rental settings");
      }
      
      const data = await response.json();
      setSettings(data);
      setHasChanges(false);
    } catch (error) {
      console.error("Error fetching rental settings:", error);
      toast.error("Kon verhuurinstellingen niet laden");
    } finally {
      setIsLoading(false);
    }
  };

  const saveRentalSettings = async () => {
    try {
      setIsSaving(true);
      const response = await fetchWithAuth(
        `${API_URL}/rental/settings`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(settings),
        },
        session
      );

      if (!response.ok) {
        throw new Error("Failed to save rental settings");
      }

      setHasChanges(false);
      toast.success("Verhuurinstellingen succesvol opgeslagen");
    } catch (error) {
      console.error("Error saving rental settings:", error);
      toast.error("Kon verhuurinstellingen niet opslaan");
    } finally {
      setIsSaving(false);
    }
  };

  const handleAdvanceHoursChange = (value: string) => {
    const numValue = parseInt(value) || 0;
    if (numValue >= -1) {
      setSettings(prev => ({ ...prev, appointmentAdvanceHours: numValue }));
      setHasChanges(true);
    }
  };

  const handleAutoSendImmowebToggle = (isSelected: boolean) => {
    setSettings(prev => ({ ...prev, autoSendImmowebInvitation: isSelected }));
    setHasChanges(true);
  };

  const handleAutoCloseToggle = (isSelected: boolean) => {
    setSettings(prev => ({ ...prev, autoCloseTerminationPhase: isSelected }));
    setHasChanges(true);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-row items-center gap-1.5">
        <Calendar className="h-[18px] w-[18px]" />
        <p className="text-lg">Verhuurinstellingen</p>
      </header>

      <Card>
        <CardBody className="gap-4">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-moss-primary" />
            <h3 className="text-base font-medium">Afspraak Planning</h3>
          </div>
          
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-neutral-primary">
              Minimale tijd vooraf (uren)
            </label>
            <Input
              type="number"
              min="-1"
              max="168"
              value={settings.appointmentAdvanceHours.toString()}
              onChange={(e) => handleAdvanceHoursChange(e.target.value)}
              placeholder="Aantal uren"
              description="Hoeveel uur van tevoren moet een afspraak minimaal gemaakt worden?"
              classNames={{
                input: "text-neutral-primary",
                description: "text-neutral-secondary-mid text-xs",
              }}
            />
          </div>

          {/* Divider */}
          <div className="border-t border-neutral-divider my-2"></div>

          <div className="flex items-center gap-2">
            <EnvelopeSimple className="h-5 w-5 text-moss-primary" />
            <h3 className="text-base font-medium">Immoweb Integratie</h3>
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-neutral-primary">
                  Automatisch uitnodigingen versturen
                </label>
                <p className="text-xs text-neutral-secondary-mid">
                  Verstuur automatisch uitnodigingen naar geïnteresseerde partijen van Immoweb
                </p>
              </div>
              <Switch
                isSelected={settings.autoSendImmowebInvitation}
                onValueChange={handleAutoSendImmowebToggle}
                classNames={{
                  wrapper: "group-data-[selected=true]:bg-moss-primary",
                }}
              />
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-neutral-divider my-2"></div>

          <div className="flex items-center gap-2">
            <ArrowsClockwise className="h-5 w-5 text-moss-primary" />
            <h3 className="text-base font-medium">Automatische Contractsluiting</h3>
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-neutral-primary">
                  Contracten automatisch sluiten
                </label>
                <p className="text-xs text-neutral-secondary-mid">
                  Sluit contracten in opzeg automatisch wanneer de einddatum verstreken is
                </p>
              </div>
              <Switch
                isSelected={settings.autoCloseTerminationPhase}
                onValueChange={handleAutoCloseToggle}
                classNames={{
                  wrapper: "group-data-[selected=true]:bg-moss-primary",
                }}
              />
            </div>

            <div className="rounded-lg border border-neutral-divider bg-gray-50 p-3 mt-1">
              <p className="text-xs font-medium text-neutral-primary mb-2">Hoe werkt dit?</p>
              <ol className="text-xs text-neutral-secondary-mid space-y-1 list-decimal list-inside">
                <li>Een contract wordt opgezegd en krijgt de status <span className="font-medium text-neutral-primary">"In opzeg"</span> met een einddatum</li>
                <li>Elke ochtend om 6:00 controleert het systeem of de einddatum verstreken is</li>
                <li>Indien verstreken, wordt het contract automatisch gesloten in zowel de database als Odoo</li>
                <li>Bij succes of falen wordt een melding verstuurd via Slack naar het verhuurkanaal</li>
              </ol>
              <div className="mt-2 rounded border border-amber-200 bg-amber-50 px-2 py-1.5">
                <p className="text-xs text-amber-800">
                  <span className="font-medium">Let op:</span> Als de sluiting in Odoo mislukt, blijft het contract in de status "In opzeg" en wordt de fout gemeld via Sentry en Slack. Het contract wordt pas gesloten wanneer Odoo-synchronisatie slaagt.
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button
              color="primary"
              variant="solid"
              className="bg-moss-primary text-white"
              onPress={saveRentalSettings}
              isLoading={isSaving}
              isDisabled={!hasChanges}
            >
              Instellingen opslaan
            </Button>

            {hasChanges && (
              <p className="text-sm text-amber-600">
                Er zijn niet-opgeslagen wijzigingen
              </p>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
};
